"use client";

import { createContext, useContext, useState, ReactNode } from "react";

type BackgroundMode = "static" | "animated";

interface BackgroundContextType {
  backgroundMode: BackgroundMode;
  setBackgroundMode: (mode: BackgroundMode) => void;
}

const BackgroundContext = createContext<BackgroundContextType | undefined>(
  undefined
);

export function BackgroundProvider({ children }: { children: ReactNode }) {
  const [backgroundMode, setBackgroundModeState] = useState<BackgroundMode>(
    () => {
      if (typeof window !== "undefined") {
        const stored = localStorage.getItem("background-mode") as BackgroundMode;
        if (stored && ["static", "animated"].includes(stored)) {
          return stored;
        }
        // Default to static if OS prefers reduced motion
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
          return "static";
        }
      }
      return "static";
    }
  );

  const setBackgroundMode = (mode: BackgroundMode) => {
    setBackgroundModeState(mode);
    localStorage.setItem("background-mode", mode);
  };

  return (
    <BackgroundContext.Provider value={{ backgroundMode, setBackgroundMode }}>
      {children}
    </BackgroundContext.Provider>
  );
}

export function useBackgroundMode() {
  const context = useContext(BackgroundContext);
  if (!context) {
    throw new Error(
      "useBackgroundMode must be used within BackgroundProvider"
    );
  }
  return context;
}
